import { Entity, UniqueEntityID } from '@domain/core'

export interface UserGeometryProps {
  name: string
  originalFileName: string
  createdAt: string
  updatedAt?: string
  // Number of features/layers in the .fgb, captured at save. Feeds the list's
  // "N camadas" description without deserializing the binary. Optional: geometries
  // saved before this field existed (and server responses until the backend echoes
  // it) have none, so the description hides that part.
  featureCount?: number
  // Local-only flags — excluded from the wire Pojo.
  deleted: boolean
  synced: boolean
  originalSynced: boolean
  processedSynced: boolean
}

export interface UserGeometryPojo {
  id: string
  name: string
  // Casing trap: the wire key is `originalFilename` (Name -> name), unlike the
  // `originalFileName` prop. Matches UserChart and the /userGeometry/sync contract.
  originalFilename: string
  createdAt: string
  updatedAt?: string
  featureCount?: number
}

export class UserGeometry extends Entity<UserGeometryProps, UserGeometryPojo> {
  private constructor(props: UserGeometryProps, id?: UniqueEntityID) {
    super(props, id)
  }

  public static create(props: UserGeometryProps, id?: UniqueEntityID): UserGeometry {
    return new UserGeometry(props, id)
  }

  public static fromPojo(pojo: UserGeometryPojo): UserGeometry {
    if (!pojo) return null

    const props: UserGeometryProps = {
      name: pojo.name,
      originalFileName: pojo.originalFilename,
      createdAt: pojo.createdAt,
      updatedAt: pojo.updatedAt,
      featureCount: pojo.featureCount,
      deleted: false,
      synced: true,
      originalSynced: true,
      processedSynced: true
    }

    return new UserGeometry(props, new UniqueEntityID(pojo.id))
  }

  public toPojo(): UserGeometryPojo {
    return {
      id: this.id.toString(),
      name: this.props.name,
      originalFilename: this.props.originalFileName,
      createdAt: this.props.createdAt,
      updatedAt: this.props.updatedAt,
      featureCount: this.props.featureCount
    }
  }

  public get name(): string {
    return this.props.name
  }

  public set name(name: string) {
    this.props.name = name
  }

  public get originalFileName(): string {
    return this.props.originalFileName
  }

  public set originalFileName(originalFileName: string) {
    this.props.originalFileName = originalFileName
  }

  public get createdAt(): string {
    return this.props.createdAt
  }

  public set createdAt(createdAt: string) {
    this.props.createdAt = createdAt
  }

  public get updatedAt(): string | undefined {
    return this.props.updatedAt
  }

  public set updatedAt(updatedAt: string | undefined) {
    this.props.updatedAt = updatedAt
  }

  public get featureCount(): number | undefined {
    return this.props.featureCount
  }

  public set featureCount(featureCount: number | undefined) {
    this.props.featureCount = featureCount
  }

  public get deleted(): boolean {
    return this.props.deleted
  }

  public set deleted(deleted: boolean) {
    this.props.deleted = deleted
  }

  public get synced(): boolean {
    return this.props.synced
  }

  public set synced(synced: boolean) {
    this.props.synced = synced
  }

  public get originalSynced(): boolean {
    return this.props.originalSynced
  }

  public set originalSynced(originalSynced: boolean) {
    this.props.originalSynced = originalSynced
  }

  public get processedSynced(): boolean {
    return this.props.processedSynced
  }

  public set processedSynced(processedSynced: boolean) {
    this.props.processedSynced = processedSynced
  }
}
